import api from "api/api";
import alertSignal from "signals/Alert.signal";
import portfolioApplicationsSignal from "signals/EdoPortfolio.signal";
import edoPortfolioSignal from "signals/EdoPortfolio.signal";
import loaderSignal from "signals/Loader.signal";
import userAccountSignal from "signals/UserAccount.signal";
import history from "../../../../history";
import $appSettings from "signals/AppSettings.signal";
import { applicationDraftSignal } from "signals/SubmitApplication.signal";

const { APPLICATION_STATUS } = $appSettings.value.constants;
const xFormAppliedFiltersToWhereClause = (options = {}) => {
  const { excludeStatusFilter = false, excludeWatchlistFilter = false } = options;

  let params = Object.fromEntries(new URLSearchParams(history.location.search));
  params = { ...params, ...edoPortfolioSignal.value.appliedFilters };

  const edoId = userAccountSignal.value.userData.account.edo.id;
  const where = {
    fundingProgram: {
      edoId,
      ...(params.programName && { id: Number(params.programName) }),
    },
  };

  if (params.id) {
    const idNumber = Number(params.id);
    if (!isNaN(idNumber) && idNumber > 0) {
      where.id = { in: [idNumber] };
    }
  }

  if (params.calcapLoanNumber) {
    where.calcapLoanNumber = {
      contains: params.calcapLoanNumber,
      mode: 'insensitive',
    };
  }

  if (params.cspAppNumber) {
    const raw = String(params.cspAppNumber).trim();
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n) && n > 0) {
      where.cspInfos = {
        some: {
          OR: [{ csiId: n }, { id: n }],
        },
      };
    }
  }

  if (params.status && !excludeStatusFilter) {
    where.status = params.status;
  }

  if (params.sediBusiness) {
    where.sediBusiness = params.sediBusiness === 'Yes';
  }

  if (params.businessId) {
    const businessId = Number(params.businessId);
    if (!isNaN(businessId) && businessId > 0) {
      where.businessId = businessId;
    }
  }

  if (params.lenderId) {
    const lenderId = Number(params.lenderId);
    if (!isNaN(lenderId) && lenderId > 0) {
      where.lenderId = lenderId
    }
  }

  const filterOrConditions = [];
  if (params.veteranOwned) {
    const value = params.veteranOwned === 'Yes' ? 'YES' : 'NO';
    const demographicsValue = params.veteranOwned === 'Yes' ? 'Y' : 'N';
    filterOrConditions.push({
      OR: [
        { business: { is: { minorityStatus: { path: ['isVeteranOwned'], equals: value } } } },
        { demographics: { is: { veteranOwned: demographicsValue } } },
      ],
    });
  }
  if (params.womanOwned) {
    const value = params.womanOwned === 'Yes' ? 'YES' : 'NO';
    const boolValue = params.womanOwned === 'Yes';
    filterOrConditions.push({
      OR: [
        { business: { is: { minorityStatus: { path: ['isWomanOwned'], equals: value } } } },
        { businessData: { is: { womenOwned: boolValue } } },
      ],
    });
  }
  if (params.minorityOwned) {
    const value = params.minorityOwned === 'Yes' ? 'YES' : 'NO';
    const boolValue = params.minorityOwned === 'Yes';
    filterOrConditions.push({
      OR: [
        { business: { is: { minorityStatus: { path: ['isMinorityOwned'], equals: value } } } },
        { businessData: { is: { minorityOwned: boolValue } } },
      ],
    });
  }
  if (filterOrConditions.length > 0) {
    where.AND = where.AND || [];
    filterOrConditions.forEach((condition) => where.AND.push(condition));
  }

  if (params.watchables && !excludeWatchlistFilter) {
    where.watchables = {
      some: {},
    };
  }

  return where;
};

const xFormSortToOrderByClause = () => {
  const sort = Object.fromEntries(
    new URLSearchParams(history.location.search)
  ).sort;

  if (!sort) {
    return undefined;
  }

  const key = sort.split('-')[0];
  const dir = sort.split('-')[1];

  if (key === 'programName') {
    return [
      {
        fundingProgram: {
          name: dir,
        },
      },
    ];
  } else {
    return [{ [key]: dir }];
  }
};

const draftsWhereClause = () => {
  const baseWhere = xFormAppliedFiltersToWhereClause({
    excludeStatusFilter: true,
    excludeWatchlistFilter: true,
  });
  return {
    ...baseWhere,
    status: APPLICATION_STATUS?.inProgress || 'IN_PROGRESS',
    submittedByEdo: true,
    isActive: true,
  };
};

export const setAndFetchEdoApplications = async (page) => {
  const { recordsPerPage } = edoPortfolioSignal.value;
  try {
    loaderSignal.update({ isContentLoading: true });

    const tallyWhereClause = xFormAppliedFiltersToWhereClause({
      excludeStatusFilter: true,
      excludeWatchlistFilter: true
    });
    const dataWhereClause = {
      ...xFormAppliedFiltersToWhereClause(),
      isActive: true,
    };

    const bundle = await api.get({
      path: '/tally/applications',
      options: {
        where: tallyWhereClause,
      },
    });

    edoPortfolioSignal.update({ bundle });

    const [data, draftsData] = await Promise.all([
      api.get({
        path: '/v2/applications',
        options: {
          getCount: true,
          skip: page ? (page - 1) * recordsPerPage : 0,
          take: recordsPerPage,
          orderBy: xFormSortToOrderByClause() || [{ id: 'desc' }],
          where: dataWhereClause,
          include: {
            watchables: true,
            business: {
              include: {
                account: true,
              },
            },
            fundingProgram: true,
            fundingProgramMembership: {
              include: {
                lender: true,
                ventureCapitalFirm: true,
              },
            },
          },
        },
      }),
      api.get({
        path: '/v2/applications',
        options: {
          getCount: true,
          where: draftsWhereClause(),
          take: 100,
          orderBy: [{ id: 'desc' }],
          include: {
            fundingProgram: { include: { referenceProgram: true } },
            business: { include: { account: true } },
          },
        },
      }),
    ]);

    const draftsFromResponse = Array.isArray(draftsData) ? draftsData : (draftsData?.applications ?? []);
    applicationDraftSignal.update({
      applicationDrafts: draftsFromResponse,
    });
    return portfolioApplicationsSignal.update({
      applications: data.applications,
      totalPages: data.count ? Math.ceil(data.count / recordsPerPage) : 1,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

// export const fetchAndSetApprovalChainUsers = application;

export const updateApplicationStatus = async (application, status) => {
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  try {
    loaderSignal.update({
      isContentLoading: true,
      message: 'Updating application status...',
    });
    const body = {
      where: {
        id: application.id,
      },
      data: {
        status,
      },
    };

    if (status === APPLICATION_STATUS.approved) {
      body.data.approvedAmount = 50000;
    }

    await api.patch({ path: '/applications', body });
    await refreshPortfolioData();
    alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully updated applications status.',
    });
    return portfolioApplicationsSignal.update({ confirmStatusVisible: false });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const openPortfolioApplicationModal = (selectedApplication) => {
  portfolioApplicationsSignal.update({
    selectedApplication,
    modalVisible: true,
  });
};

const refreshPortfolioData = async () => {
  await setAndFetchEdoApplications();
  const applicationId =
    portfolioApplicationsSignal.value.selectedApplication.id;
  const { applications } = portfolioApplicationsSignal.value;

  return portfolioApplicationsSignal.update({
    selectedApplication: applications.find((app) => app.id === applicationId),
  });
};

export const handleEdoPortfolioFilterChange = (key, value) => {
  const dropdownFilters = portfolioApplicationsSignal.value.dropdownFilters;
  return portfolioApplicationsSignal.update({
    dropdownFilters: { ...dropdownFilters, [key]: value },
  });
};

export const handleEdoPortfolioAppliedFiltersChange = (appliedFilters) =>
  portfolioApplicationsSignal.update({
    appliedFilters: {
      ...appliedFilters,
    },
  });

export const handleDownloadApplicationsData = async () => {
  loaderSignal.update({ isContentLoading: true });
  try {
    await api.get({
      path: '/applications/download',
      options: {
        orderBy: xFormSortToOrderByClause(),
        where: xFormAppliedFiltersToWhereClause(),
      },
      isFileDownload: true,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const resetEdoPortfolioFilterInputs = () =>
  portfolioApplicationsSignal.update({ dropdownFilters: {}, appliedFilters: {} });
